import { Button, CompanyLogo, ProgressBar, SectionCard } from '../../../components/ui';
import { formatBRLFromCents, formatCount } from '../../../lib/format';
import type { CompanySummary } from '../../../types';

/**
 * Ranking das empresas pelo que os disparos recuperaram no periodo
 * (WhatsApp + e-mail), com a comissao e o custo por recuperacao ao lado.
 *
 * Decisoes nao obvias:
 *  1. A barra e relativa a PRIMEIRA do ranking (a melhor = 100%): a leitura
 *     e "quanto falta para chegar na melhor", nao a fatia do total.
 *  2. Top 5 com "ver todas" levando a tabela de envios filtrada: com
 *     centenas de empresas, um ranking de 300 linhas nao e ranking.
 *  3. Empresa sem retorno no periodo aparece no fim com o texto dizendo
 *     isso, nunca com "R$ 0,00" solto -- zero de nada nao e desempenho.
 */

export interface LinhaDoRanking {
  readonly companyId: string;
  readonly recuperadoCents: number;
  readonly comissaoCents: number;
  readonly pagaram: number;
  readonly custoPorRecuperacaoCents: number | null;
  readonly envios: number;
}

const TOP = 5;

export function RankingDeEmpresas({
  linhas,
  companies,
  onAbrir,
  className = '',
}: {
  readonly linhas: readonly LinhaDoRanking[];
  readonly companies: readonly CompanySummary[];
  readonly onAbrir: (companyId: string) => void;
  readonly className?: string;
}): JSX.Element {
  const ordenadas = [...linhas].sort((a, b) => b.recuperadoCents - a.recuperadoCents);
  const top = ordenadas.slice(0, TOP);
  const melhor = top[0]?.recuperadoCents ?? 0;

  return (
    <SectionCard id="ranking-empresas" icon="estrela" title="Quem mais recuperou" description="Empresas pelo recuperado com disparos no período (WhatsApp e e-mail)." className={className}>
      {top.length === 0 ? (
        <p className="rounded-xl border border-dashed border-edge bg-elevated px-4 py-6 text-center text-sm text-ink-muted">Nenhum disparo no período. Os públicos parados estão no radar logo abaixo.</p>
      ) : (
        <ol className="flex flex-col gap-3">
          {top.map((l, i) => {
            const empresa = companies.find((c) => c.id === l.companyId) ?? null;
            return (
              <li key={l.companyId} className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="w-4 shrink-0 text-right text-xs font-semibold tabular-nums text-ink-subtle">{i + 1}</span>
                    {empresa === null ? null : <CompanyLogo company={empresa} size="sm" />}
                    <span className="truncate text-sm font-medium text-ink">{empresa?.name ?? 'Empresa'}</span>
                  </div>
                  <Button variant="ghost" size="sm" iconRight="seta-dir" onClick={() => onAbrir(l.companyId)} title={`Abrir os envios de ${empresa?.name ?? 'empresa'}`} aria-label={`Abrir os envios de ${empresa?.name ?? 'empresa'}`}>
                    Abrir
                  </Button>
                </div>
                {l.recuperadoCents === 0 ? (
                  <p className="pl-6 text-xs text-ink-subtle">Sem retorno de disparo no período ({formatCount(l.envios)} {l.envios === 1 ? 'envio' : 'envios'}).</p>
                ) : (
                  <div className="flex flex-col gap-1 pl-6">
                    <ProgressBar value={l.recuperadoCents} max={Math.max(1, melhor)} tone="good" label={`Recuperado por ${empresa?.name ?? 'empresa'}`} />
                    <p className="flex flex-wrap justify-between gap-2 text-xs tabular-nums">
                      <span className="font-medium text-ink">{formatBRLFromCents(l.recuperadoCents)}</span>
                      <span className="text-ink-subtle">
                        comissão {formatBRLFromCents(l.comissaoCents)} · {l.custoPorRecuperacaoCents === null ? 'sem custo' : `${formatBRLFromCents(l.custoPorRecuperacaoCents)} por recuperação`}
                      </span>
                    </p>
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}
      {ordenadas.length > TOP ? <p className="mt-3 text-xs text-ink-subtle">E mais {formatCount(ordenadas.length - TOP)} empresas: abra os envios para ver todas.</p> : null}
    </SectionCard>
  );
}
